import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import withDashboardLayout from "../../hoc/withDashboardLayout";
import { CaretDownFilled, DoubleLeftOutlined } from "@ant-design/icons";
import { Button, Dropdown, Empty, Modal, message } from "antd";
import {
  GetAppointmentsByClientId,
  GetClient,
  UpdateClient,
} from "../../utils/api";
import dayjs from "dayjs";
import Loader from "../shared/Loader";
import MaterialTable from "material-table";
import tableIcons from "../../utils/tableIcons";
import { imgPath } from "../../utils/axiosInstance";
import PhoneIcon from "../../assets/phone-icon.png";
import MailIcon from "../../assets/mail-icon.png";

const items = [
  {
    label: "Active",
    key: "active",
  },
  {
    label: "Block",
    key: "blocked",
  },
];

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clientData, setClientData] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [modalData, setModalData] = useState({
    visible: false,
    selectedStatus: null,
    user: null,
    id: null,
    action: null,
  });

  const tableRef = useRef(null);

  const getClientById = async () => {
    setLoading(true);
    try {
      const res = await GetClient(id);
      if (res?.isSuccess) {
        setClientData(res?.data);
        getAppointments(res?.data?.id);
      } else {
        message.error("Error while fetching details, Please try again");
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const getAppointments = async (userId) => {
    try {
      const response = await GetAppointmentsByClientId(userId);
      if (response?.isSuccess) {
        setAppointments(response?.items);
        setLoading(false);
      } else {
        setLoading(false);
        message.error(response?.error);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Order Id",
      field: "uniqueId",
      sorting: false,
      cellStyle: { width: "12%" },
      render: (record) => {
        return <span>#{record?.uniqueId}</span>;
      },
    },
    {
      title: "Business Name",
      field: "businessName",
      sorting: false,
      cellStyle: { width: "12%" },
      render: (record) => {
        return <span>{record?.service?.businessProfile?.business_name}</span>;
      },
    },
    {
      title: "User Name",
      field: "userName",
      sorting: false,
      cellStyle: { width: "12%" },
      render: (record) => {
        return <span>{record?.client?.name}</span>;
      },
    },
    {
      title: "Date & Time",
      field: "dateTime",
      sorting: false,
      cellStyle: { width: "12%" },
      render: (record) => {
        return <span>{dayjs(record?.date).format("DD MMM YYYY")}</span>;
      },
    },
    {
      title: "Service Name",
      field: "serviceName",
      sorting: false,
      cellStyle: { width: "12%" },
      render: (record) => {
        return <span>{record?.service?.serviceName}</span>;
      },
    },
    {
      title: "Payment Type",
      field: "payType",
      sorting: false,
      cellStyle: { width: "12%" },
      render: (record) => {
        return (
          <span>
            {record?.payType === "payInAdvance" ? "Pay In Advance" : "Deposit"}
          </span>
        );
      },
    },
    {
      title: "Status",
      field: "status",
      sorting: false,
      cellStyle: { width: "12%" },
      render: (record) => {
        return (
          <span
            className={`capitalize ${
              record?.status === "finished"
                ? "text-[green]"
                : record?.status === "upcoming"
                ? "text-yellow-500"
                : "text-red-900"
            }`}
          >
            {/* {record?.status === "upcoming"
              ? "In progress"
              : record?.status === "finished"
              ? "Completed"
              : record?.status} */}
            {record?.status === "finished" ? "Completed" : record?.status}
          </span>
        );
      },
    },
  ];

  let updatedItems = items.filter((item) => {
    return item.key !== clientData?.statusType;
  });

  if (clientData?.statusType === "blocked") {
    updatedItems = [
      {
        key: "unblocked",
        label: "Unblock",
      },
    ];
  }
  if (clientData?.statusType === "active") {
    updatedItems = [
      // {
      //     label: 'Inactive',
      //     key: 'inactive',
      // },
      {
        label: "Block",
        key: "blocked",
      },
    ];
  }
  if (clientData?.statusType === "inactive") {
    updatedItems = [
      {
        label: "Active",
        key: "active",
      },
      {
        label: "Block",
        key: "blocked",
      },
    ];
  }

  const handleActionModalOpen = (action, status, data) => {
    if (action === "changeStatus") {
      setModalData({
        visible: true,
        user: data.name,
        selectedStatus: status,
        id: data.id,
        action: "changeStatus",
      });
    }
  };

  const handleChangeStatusOk = async () => {
    if (modalData.action === "changeStatus") {
      try {
        setBtnLoading(true);
        const res = await UpdateClient(
          {
            statusType:
              modalData?.selectedStatus === "unblocked"
                ? "active"
                : modalData?.selectedStatus,
          },
          modalData?.id
        );
        setBtnLoading(false);
        if (res?.isSuccess) {
          message.success("Client status updated successfully");
          setModalData({
            selectedStatus: null,
            user: null,
            id: null,
            visible: false,
            action: null,
          });
          getClientById(id);
        }
      } catch (error) {
        setBtnLoading(false);
        message.error("Error in updating status, Try again later");
      }
    }
  };

  const handleModalCancel = () => {
    setModalData({
      visible: false,
      selectedStatus: null,
      user: null,
      id: null,
      action: null,
    });
  };

  const handleBack = () => {
    navigate("/admin/clients");
  };

  useEffect(() => {
    getClientById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="w-full h-full">
      <div className="w-[95%] mx-auto">
        <div className="w-full py-4 gap-x-2 flex items-center back-icon">
          <h1 className="font-semibold text-lg cursor-pointer">Clients</h1>
          <DoubleLeftOutlined onClick={handleBack} />
          <h1 className="font-semibold text-lg text-black-300">
            Client Details
          </h1>
        </div>

        {/* Client Details */}
        <div className="w-full bg-white min-h-[calc(100vh-110px)] flex flex-col gap-y-4 box-shadow rounded-t-2xl pt-5">
          <div className="w-full px-5">
            <div className="border border-red-100 rounded-lg p-4">
              <div className="w-full flex justify-between">
                <div className="flex flex-col">
                  <div className="flex items-start gap-3">
                    {clientData.image ? (
                      <img
                        src={imgPath + clientData.image}
                        alt={clientData.name}
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#FDE3E4] font-semibold text-primary-600">
                        {clientData.name
                          .split(" ")
                          .map((word) => word[0].toUpperCase())
                          .join("")
                          .slice(0, 2)}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium leading-none text-[#24242E]">
                        {clientData?.name}
                      </p>
                    </div>
                  </div>
                  <div className="text-lightGray text-xs pt-2 flex flex-col gap-1">
                    <div className="flex items-center gap-x-2">
                      <img
                        src={PhoneIcon}
                        alt="phone-icon"
                        className="w-3 h-3"
                      />
                      <span>+44 {clientData?.phone_number}</span>
                    </div>
                    <div className="flex items-center gap-x-2">
                      <img src={MailIcon} alt="mail-icon" className="w-3 h-3" />
                      <span>{clientData?.email}</span>
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div>
                  {
                    <div className="cursor-pointer">
                      <Dropdown
                        menu={{
                          items: updatedItems,
                          onClick: (e) =>
                            handleActionModalOpen(
                              "changeStatus",
                              e?.key,
                              clientData
                            ),
                        }}
                        trigger={["click"]}
                      >
                        <div
                          className={`flex items-center justify-end gap-x-1 border px-2 py-1 rounded-md ${
                            clientData?.statusType === "active"
                              ? "border-[green] bg-[#e5ffe4]"
                              : clientData?.statusType === "inactive"
                              ? "border-orange bg-[#ffefd9]"
                              : clientData?.statusType === "blocked"
                              ? "border-[red] bg-[#fff6f6]"
                              : "border-black-900"
                          }`}
                        >
                          <span
                            className={`capitalize text-xs ${
                              clientData?.statusType === "active"
                                ? "text-[green]"
                                : clientData?.statusType === "inactive"
                                ? "text-orange"
                                : clientData?.statusType === "blocked"
                                ? "text-[red]"
                                : "text-black-900"
                            }`}
                          >
                            {clientData?.statusType}
                          </span>
                          <button className="text-[10px]">
                            <CaretDownFilled />
                          </button>
                        </div>
                      </Dropdown>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Appointments Table */}
          <div className="search-custom-space main-scroll w-full min-h-[calc(100vh-170px)] bg-white h-[380px] overflow-scroll flex flex-col relative">
            <h1 className="font-semibold pl-6 text-[#2D2D37]">
              Appointments List
            </h1>
            {appointments && appointments?.length > 0 ? (
              <MaterialTable
                title="Support Tickets"
                tableRef={tableRef}
                columns={columns}
                icons={tableIcons}
                options={{
                  showTitle: false,
                  draggable: false,
                  sorting: true,
                  paging: false,
                  pageSize: 25,
                  debounceInterval: 700,
                  padding: "dense",
                  paginationType: "stepped",
                  searchFieldVariant: "outlined",
                  searchFieldAlignment: "left",
                }}
                data={appointments}
              />
            ) : (
              <div className="w-full text-center my-16 font-medium text-primary-900 text-xl">
                <Empty />
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        centered
        title={
          modalData.action === "delete"
            ? "Confirm Delete"
            : modalData?.action === "changeStatus"
            ? "Confirm Status Change"
            : "Confirm Selection Delete"
        }
        open={modalData.visible}
        onCancel={handleModalCancel}
        footer={
          <div>
            <Button onClick={handleModalCancel}>Cancel</Button>
            <Button
              type="primary"
              className="text-white"
              onClick={
                modalData.action === "delete" ? undefined : handleChangeStatusOk
              }
            >
              Ok
              {btnLoading && (
                <span className="loader ml-2 align-middle mb-1"></span>
              )}
            </Button>
          </div>
        }
      >
        {modalData.action === "changeStatus"
          ? `Are you sure you want to change the status of ${modalData.user} to ${modalData.selectedStatus}?`
          : modalData.action === "delete"
          ? `Are you sure you want to delete ${modalData.user}?`
          : "Are you sure you want to delete the selected clients?"}
      </Modal>
    </div>
  );
};

export default withDashboardLayout(ClientDetails);
